'use client';

import React from 'react';
import { Tooltip } from 'src/components/PulseUI';
import { TypeIcon } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import {
  faExternalLinkAlt,
  faQuestionCircle,
  faCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { useLogging } from 'src/context/logging';
import Checkbox from 'src/components/PulseUI/Checkbox/Checkbox';
import { getProfileUrl } from 'src/utils/profileUrl';

interface LocationRowItemProps {
  location: {
    provider_id: string;
    type: string;
    name: string;
    claims: string;
    percentage: number;
    obfuscated: boolean;
    specialty: string;
    primary_address: {
      city: string;
      state: string;
    };
  };
  // Bulk selection props
  showSelection?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (selected: boolean) => void;
  // Testing props
  'data-testid'?: string;
}

const CardLocationRow = ({
  location,
  showSelection = false,
  isSelected = false,
  onSelectionChange,
  'data-testid': dataTestId,
}: LocationRowItemProps) => {
  const log = useLogging();
  const percentage =
    location?.percentage || location?.percentage === 0
      ? `${location?.percentage}%`
      : null;

  const name = location?.name || 'N/A';
  const isLessThanEleven = location?.claims && location?.claims === '<11';
  const specialty = location?.specialty || 'N/A';
  const cityState =
    location.primary_address?.city && location.primary_address?.state
      ? `${location?.primary_address?.city}, ${location?.primary_address?.state}`
      : 'N/A';

  return (
    <>
      <div
        className="flex h-20 justify-between items-center w-full min-w-0 rounded-lg border
         border-neutral-300 bg-level-1 overflow-hidden"
        data-testid={dataTestId}
      >
        <div className="group flex relative flex-1 items-center h-full whitespace-nowrap overflow-hidden">
          <div
            className="flex items-center gap-4 h-full px-4 cursor-pointer flex-1 min-w-0 
            overflow-hidden group-hover:bg-neutral-100"
          >
            {/* Selection checkbox */}
            {showSelection && (
              <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => onSelectionChange?.(e.target.checked)}
                  size="sm"
                  variant="solid"
                  color="primary"
                  className="flex-shrink-0"
                />
              </div>
            )}

            <TypeIcon type={location.type} size="lg" className="text-link" />

            <div className="flex flex-col justify-center min-w-0 flex-1 overflow-hidden gap-1">
              <Link
                id={`top-providers-provider-${location?.provider_id}`}
                href={getProfileUrl(location?.type, location?.provider_id)}
                scroll={false}
                onClick={(e) => {
                  e.stopPropagation();
                  log.event('Location Clicked', {
                    source: 'web',
                    location: 'VolumeByLocation',
                    provider_id: location?.provider_id,
                  });
                }}
                className="flex items-center text-link no-underline min-w-0"
              >
                <span className="text-sm font-medium truncate flex-1">
                  {name}
                  <Icon icon={faExternalLinkAlt} className="ml-1 text-xs" />
                </span>
              </Link>
              <div className="flex gap-2 items-center w-full min-w-0">
                <span className="text-xs text-neutral-600 font-medium truncate max-w-[40%]">
                  {specialty || 'n/a'}
                </span>
                <Icon
                  icon={faCircle}
                  className="text-[6px] flex-shrink-0 text-slate-300"
                />
                <span className="text-xs text-neutral-600 font-medium truncate flex-1 min-w-0">
                  {cityState || 'n/a'}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col h-full px-4 justify-center items-end bg-teal-050 flex-shrink-0 w-20">
          <Tooltip
            content="Data can be any number 1 to 10"
            position="top"
            showOnCondition={isLessThanEleven}
          >
            <span
              className={`text-sm text-teal-900 font-semibold ${
                isLessThanEleven ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              {isLessThanEleven
                ? location?.claims
                : Number(location?.claims)?.toLocaleString()}
            </span>
          </Tooltip>
          <Tooltip
            content="of total Site volume"
            position="top"
          >
            {percentage ? (
              <span className="text-sm text-teal-900 cursor-pointer font-normal">
                {percentage}
              </span>
            ) : (
              <span className="text-sm">
                <Icon
                  icon={faQuestionCircle}
                  size="sm"
                  className="text-neutral-600"
                />
              </span>
            )}
          </Tooltip>
        </div>
      </div>
    </>
  );
};

export default CardLocationRow;
